import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Archive, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import NegotiatorModal from './NegotiatorModal';

interface Negotiator {
  id: string;
  firstname: string;
  lastname: string;
  role: string;
  status: string;
  archived: boolean;
  arrival_date: string | null;
  departure_date: string | null;
  has_custom_objectives: boolean;
  monthly_objectives: Record<string, Record<string, number>> | null;
  agencies: {
    name: string;
    start_date?: string | null;
    end_date?: string | null;
    objective_percentage?: number;
  }[];
  annual_objectives?: {
    fiscal_year: number;
    annual_objective: number;
  }[];
}

export default function NegotiatorSettings() {
  const [negotiators, setNegotiators] = useState<Negotiator[]>([]);
  const [filteredNegotiators, setFilteredNegotiators] = useState<Negotiator[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNegotiator, setSelectedNegotiator] = useState<Negotiator | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Calculate current fiscal year
  const currentFiscalYear = (() => {
    const today = new Date();
    const month = today.getMonth() + 1; // getMonth() returns 0-11
    return month <= 4 ? today.getFullYear() - 1 : today.getFullYear();
  })();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterAndSortNegotiators();
  }, [searchTerm, negotiators, showArchived]);

  const loadData = async () => {
    try {
      console.log('Loading negotiators data...');
      const { data: negotiatorsData, error: negotiatorsError } = await supabase
        .from('negotiators')
        .select(`
          id,
          firstname,
          lastname,
          role,
          status,
          archived,
          arrival_date,
          departure_date,
          has_custom_objectives,
          monthly_objectives,
          negotiator_annual_objectives (
            fiscal_year,
            annual_objective
          ),
          negotiator_agencies (
            agency
          )
        `)
        .order('lastname')
        .order('firstname');

      if (negotiatorsError) throw negotiatorsError;

      console.log('Loaded negotiators:', negotiatorsData);

      const formattedData = (negotiatorsData || []).map(n => ({
        id: n.id,
        firstname: n.firstname,
        lastname: n.lastname,
        role: n.role || 'négociateur',
        status: n.status || 'conseiller',
        archived: n.archived || false,
        arrival_date: n.arrival_date,
        departure_date: n.departure_date,
        has_custom_objectives: n.has_custom_objectives || false,
        monthly_objectives: n.monthly_objectives,
        annual_objectives: n.negotiator_annual_objectives,
        agencies: (n.negotiator_agencies || []).map(na => ({
          name: na.agency
        }))
      }));

      console.log('Formatted negotiators:', formattedData);

      setNegotiators(formattedData);
      setFilteredNegotiators(formattedData);
    } catch (err: any) {
      console.error('Error loading negotiators:', err);
      setError(err.message);
    }
  };

  const filterAndSortNegotiators = () => {
    const filtered = negotiators.filter(negotiator => {
      const fullName = `${negotiator.firstname} ${negotiator.lastname}`.toLowerCase();
      const agencies = negotiator.agencies.map(a => a.name.toLowerCase());
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = fullName.includes(searchLower) || 
                          agencies.some(agency => agency.includes(searchLower));
      const matchesArchived = showArchived ? negotiator.archived : !negotiator.archived;
      
      return matchesSearch && matchesArchived;
    });
    setFilteredNegotiators(filtered);
  };

  const handleEditClick = (negotiator: Negotiator) => {
    console.log('Editing negotiator:', negotiator);
    setSelectedNegotiator(negotiator);
    setIsModalOpen(true);
  };

  const handleAddClick = () => {
    setSelectedNegotiator(null);
    setIsModalOpen(true);
  };

  const handleToggleArchive = async (negotiator: Negotiator) => {
    try {
      const { error } = await supabase
        .from('negotiators')
        .update({ archived: !negotiator.archived })
        .eq('id', negotiator.id);

      if (error) throw error;
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteNegotiator = async (negotiator: Negotiator) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer ${negotiator.firstname} ${negotiator.lastname} ?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('negotiators')
        .delete()
        .eq('id', negotiator.id);

      if (error) throw error;
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getRoleClass = (role: string) => {
    switch (role) {
      case 'directeur':
        return 'bg-purple-100 text-purple-800';
      case 'responsable':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'expert':
        return 'bg-red-100 text-red-800';
      case 'senior':
        return 'bg-orange-100 text-orange-800';
      case 'confirmé':
        return 'bg-yellow-100 text-yellow-800';
      case 'animateur':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  // Function to get the annual objective for the current fiscal year
  const getCurrentFiscalYearObjective = (negotiator: Negotiator): number => {
    // If using custom monthly objectives for current fiscal year
    if (negotiator.has_custom_objectives && negotiator.monthly_objectives?.[currentFiscalYear]) {
      return Object.values(negotiator.monthly_objectives[currentFiscalYear])
        .reduce((sum, value) => sum + (value || 0), 0);
    }
    
    // Otherwise, get the annual objective from negotiator_annual_objectives
    const annualObjective = negotiator.annual_objectives?.find(
      obj => obj.fiscal_year === currentFiscalYear
    );
    return annualObjective?.annual_objective || 0;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Rechercher un négociateur ou une agence…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
          />
        </div>
        <button
          onClick={() => setShowArchived(!showArchived)}
          className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border transition-colors ${
            showArchived
              ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100'
              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          <Archive size={15} />
          <span>{showArchived ? 'Voir les actifs' : 'Voir les archivés'}</span>
        </button>
        <button
          onClick={handleAddClick}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 text-sm font-semibold transition-colors shadow-sm"
        >
          <Plus size={16} />
          <span>Nouveau négociateur</span>
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">{error}</div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-100">
          <span className="text-sm font-semibold text-slate-700">Négociateurs</span>
          <span className="text-xs text-slate-400">{filteredNegotiators.length} résultat{filteredNegotiators.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Nom</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Prénom</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Objectif annuel</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Fonction</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Agences</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Arrivée</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Départ</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {filteredNegotiators.map((negotiator) => (
                <tr
                  key={negotiator.id}
                  className={`border-b border-slate-100 last:border-0 transition-colors ${
                    negotiator.archived ? 'bg-slate-50/60 hover:bg-slate-100/60' : 'hover:bg-indigo-50/30'
                  }`}
                >
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-slate-800">{negotiator.lastname}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">{negotiator.firstname}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(getCurrentFiscalYearObjective(negotiator))}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${getRoleClass(negotiator.role)}`}>
                      {negotiator.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${getStatusClass(negotiator.status)}`}>
                      {negotiator.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    <div className="flex flex-wrap gap-1">
                      {negotiator.agencies.map((agency) => (
                        <span key={agency.name} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{agency.name}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">{formatDate(negotiator.arrival_date)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">{formatDate(negotiator.departure_date)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleEditClick(negotiator)}
                        className="p-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-colors"
                        title="Modifier le négociateur"
                      >
                        <Edit size={15} />
                      </button>
                      <button
                        onClick={() => handleToggleArchive(negotiator)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          negotiator.archived
                            ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-600'
                            : 'bg-orange-50 hover:bg-orange-100 text-orange-600'
                        }`}
                        title={negotiator.archived ? 'Afficher dans les listes' : 'Masquer des listes'}
                      >
                        {negotiator.archived ? <Eye size={15} /> : <EyeOff size={15} />}
                      </button>
                      <button
                        onClick={() => handleDeleteNegotiator(negotiator)}
                        className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                        title="Supprimer le négociateur"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <NegotiatorModal
        negotiator={selectedNegotiator}
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedNegotiator(null); }}
        onSuccess={loadData}
      />
    </div>
  );
}